import { useState, useRef } from "react";
import { X, Camera } from "lucide-react";
import ChatHeader from "./ChatHeader";

type ImageItem = { base64: string; mediaType: string; preview: string };

interface PhotoIntakeModalProps {
  onClose: () => void;
  onAnalyze: (
    apiPrompt: string,
    imageData: { base64: string; mediaType: string },
    displayText: string,
    allImages?: { base64: string; mediaType: string }[]
  ) => void;
  title: string;
  subtitle: React.ReactNode;
  disclaimer?: string;
  analysisPrompt: string;
  displayText: string;
  ctaText?: string;
  multiple?: boolean;
}

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const MAX_IMAGES = 8;

const PhotoIntakeModal = ({
  onClose,
  onAnalyze,
  title,
  subtitle,
  disclaimer,
  analysisPrompt,
  displayText,
  ctaText = "Analyze this 🔍",
  multiple = false,
}: PhotoIntakeModalProps) => {
  const [images, setImages] = useState<ImageItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const readFile = (file: File): Promise<ImageItem> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve({
          base64: result.split(",")[1],
          mediaType: file.type || "image/jpeg",
          preview: result,
        });
      };
      reader.onerror = () => reject(new Error("read failed"));
      reader.readAsDataURL(file);
    });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length === 0) return;

    const oversized = files.some((f) => f.size > MAX_IMAGE_SIZE);
    if (oversized) {
      setError("One of those images is a little too big. Try smaller ones or new photos. 💜");
      return;
    }
    setError(null);

    try {
      const newOnes = await Promise.all(files.map(readFile));
      setImages((prev) => {
        const combined = multiple ? [...prev, ...newOnes] : newOnes.slice(0, 1);
        if (multiple && combined.length > MAX_IMAGES) {
          setError(`You can add up to ${MAX_IMAGES} photos at a time.`);
          return combined.slice(0, MAX_IMAGES);
        }
        return combined;
      });
    } catch {
      setError("Couldn't read that image. Try another one. 💜");
    }
  };

  const removeImage = (idx: number) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAnalyze = () => {
    if (images.length === 0) return;
    const stripped = images.map(({ base64, mediaType }) => ({ base64, mediaType }));
    onAnalyze(analysisPrompt, stripped[0], displayText, stripped);
  };

  const hasImages = images.length > 0;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col" style={{ backgroundColor: "#F0F0FF" }}>
      {/* Header */}
      <ChatHeader onLogoClick={onClose} />

      <div
        className="flex-1 overflow-y-auto px-4 pb-6 max-w-lg mx-auto w-full relative"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 84px)" }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-2 flex items-center justify-center"
          style={{
            top: "calc(env(safe-area-inset-top, 0px) + 92px)",
            width: 44,
            height: 44,
          }}
        >
          <X size={24} style={{ color: "#54525C" }} />
        </button>
        <h2 className="font-display" style={{ fontSize: 22, color: "#1C1A3E" }}>{title}</h2>
        <p className="font-body mb-3" style={{ fontSize: 13, color: "#5050C8", paddingRight: 48 }}>
          {subtitle}
        </p>

        {disclaimer && (
          <div
            className="mx-auto mt-3 mb-6 max-w-md rounded-xl px-4 py-3 border-l-4"
            style={{
              backgroundColor: "#E8E8FB",
              borderLeftColor: "#5050C8",
            }}
          >
            <p className="text-sm font-semibold leading-relaxed" style={{ color: "#1C1A3E" }}>
              {disclaimer}
            </p>
          </div>
        )}
        {!disclaimer && <div className="mb-3" />}

        {!hasImages && (
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-3 py-12"
              style={{
                border: "2px dashed #C8C8F4",
                borderRadius: 16,
                backgroundColor: "#F8F8FF",
              }}
            >
              <Camera size={32} style={{ color: "#5050C8" }} />
              <span className="font-body" style={{ fontSize: 14, color: "#54525C" }}>
                {multiple ? "Tap to upload or take photos" : "Tap to upload or take a photo"}
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/*"
              multiple={multiple}
              onChange={handleFileSelect}
              className="hidden"
            />
          </>
        )}

        {hasImages && (
          <div className="flex flex-col gap-3">
            {multiple ? (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  {images.map((img, idx) => (
                    <div key={idx} style={{ position: "relative" }}>
                      <img
                        src={img.preview}
                        alt={`Page ${idx + 1}`}
                        className="w-full object-cover"
                        style={{ height: 96, borderRadius: 10 }}
                      />
                      <button
                        onClick={() => removeImage(idx)}
                        aria-label="Remove photo"
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          background: "rgba(28,26,62,0.75)",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                  {images.length < MAX_IMAGES && (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex flex-col items-center justify-center gap-1"
                      style={{
                        height: 96,
                        border: "2px dashed #C8C8F4",
                        borderRadius: 10,
                        backgroundColor: "#F8F8FF",
                        color: "#5050C8",
                      }}
                    >
                      <Camera size={20} />
                      <span className="font-body" style={{ fontSize: 11 }}>Add more</span>
                    </button>
                  )}
                </div>
                <p className="font-body" style={{ fontSize: 12, color: "#54525C" }}>
                  {images.length} photo{images.length !== 1 ? "s" : ""} added · they'll be read together as one recipe
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/*"
                  multiple={multiple}
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </>
            ) : (
              <img
                src={images[0].preview}
                alt="Selected"
                className="w-full object-cover"
                style={{ maxHeight: 220, borderRadius: 12 }}
              />
            )}

            <button
              onClick={handleAnalyze}
              className="w-full font-body font-semibold text-white rounded-2xl py-3.5 transition-transform active:scale-[0.98]"
              style={{
                background: "linear-gradient(135deg, #5050C8, #1C1A3E)",
                fontSize: 15,
              }}
            >
              {ctaText}
            </button>
            <button
              onClick={() => { setImages([]); setError(null); }}
              className="font-body text-center underline"
              style={{ color: "#54525C", fontSize: 13 }}
            >
              {multiple ? "Start over" : "Choose a different photo"}
            </button>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 rounded-2xl" style={{ backgroundColor: "#FFF0F0", border: "1px solid #FFD0D0" }}>
            <p className="font-body text-sm" style={{ color: "#CC3333" }}>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoIntakeModal;